import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { workflowAPI } from '../../lib/api';
import { errMsg } from '../../lib/workflow';
import { QueueSection, StatTile } from '../workflow/QueueSection';
import { QueueCard } from '../workflow/QueueCard';
import { HomeHeader } from './HomeHeader';

const btn = 'h-8 rounded-xl text-xs font-bold transition-transform active:scale-95';

export const NiveditHome = ({ data, user, embedded }) => {
  const { summary, items, reload } = data;
  const navigate = useNavigate();
  const [busyId, setBusyId] = useState(null);
  const of = (...st) => items.filter((i) => st.includes(i.stage));
  const changes = of('changes_requested');
  const todo = of('new', 'in_visuals');
  const waiting = of('in_review');

  const submit = async (item) => {
    setBusyId(item.id);
    try {
      await workflowAPI.submitReview(item.id);
      toast.success('Sent to Nayan for review 👀🚀');
      reload();
    } catch (e) {
      toast.error(errMsg(e, 'Could not submit for review'));
    } finally {
      setBusyId(null);
    }
  };

  const actions = (it) => (
    <>
      <Button onClick={() => navigate(`/etsy/${it.id}`)} className={`${btn} bg-[#A855F7] hover:bg-[#9333EA] text-white`} data-testid={`nivedit-upload-button-${it.id}`}>
        {it.media_count ? 'Open & edit visuals 🎨' : 'Upload visuals 📸'}
      </Button>
      <Button
        onClick={() => submit(it)}
        disabled={!it.media_count || busyId === it.id}
        title={it.media_count ? '' : 'Upload at least one photo first'}
        className={`${btn} bg-[#E07A5F] hover:bg-[#D0694E] text-white disabled:opacity-40`}
        data-testid={`nivedit-submit-review-button-${it.id}`}
      >
        {busyId === it.id ? <Loader2 size={12} className="mr-1 animate-spin" /> : null} Submit for review 🚀
      </Button>
    </>
  );

  return (
    <div className="space-y-8" data-testid="nivedit-home">
      {!embedded && (
        <HomeHeader emoji="📸" name={user?.name} subtitle="Pick a link, create stunning visuals and send it to Nayan for review. Changes requested always come first ✨" />
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile emoji="🔁" value={changes.length} label="Changes to fix" testId="nivedit-stat-changes" tone="bg-[#FFF1F2]" />
        <StatTile emoji="🔗" value={todo.length} label="Links to work on" testId="nivedit-stat-todo" />
        <StatTile emoji="👀" value={waiting.length} label="Waiting on Nayan" testId="nivedit-stat-waiting" />
        <StatTile emoji="⚡" value={summary?.today?.sent_for_review || 0} label="Sent for review today" testId="nivedit-stat-sent-today" tone="bg-[#F3E8FF]/50" />
      </div>

      <QueueSection
        emoji="🔁" title="Changes requested by Nayan" testId="nivedit-changes-queue"
        subtitle="Do these first. Fix the visuals, then resubmit."
        items={changes} empty="No changes requested. Great work! 🙌"
        render={(it, i) => <QueueCard key={it.id} item={it} index={i}>{actions(it)}</QueueCard>}
      />
      <QueueSection
        emoji="🔗" title="New links to work on" testId="nivedit-todo-queue"
        subtitle="Fresh from Nayan, with his notes. Oldest first."
        items={[...todo].reverse()} empty="All caught up! New links from Nayan will pop up here 🎈"
        render={(it, i) => <QueueCard key={it.id} item={it} index={i}>{actions(it)}</QueueCard>}
      />
      <QueueSection
        emoji="👀" title="Waiting on Nayan's review" testId="nivedit-waiting-queue"
        items={waiting} empty="Nothing waiting on review"
        render={(it, i) => <QueueCard key={it.id} item={it} index={i} showNotes={false} />}
      />
    </div>
  );
};
